/**
 * ChatbotAdminPage
 * 
 * Admin page for reviewing chatbot feedback and managing knowledge base.
 * Features:
 * - View all user feedback (thumbs up/down)
 * - Filter by helpful/not helpful
 * - See conversation context
 * - Mark feedback as reviewed
 * - Add knowledge entries based on common questions
 * - View usage statistics
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { useChatbot } from '../components/chatbot/ChatbotContext';
import { hasPermission, FEATURE_PERMISSIONS, canViewAllData } from '../utils/permissions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  setDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { sanitizeString } from '../utils/sanitization';
import { logUserAction, logEntityChanges, logEntityDeletion } from '../utils/logger';
import Tooltip from '../components/common/Tooltip';
import Toast from '../components/common/Toast';
import PromoteToKBModal from '../components/chatbot/PromoteToKBModal';
import KnowledgeBaseList from '../components/chatbot/KnowledgeBaseList';


// Generate a URL-safe slug from title for document ID
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '_')          // Spaces to underscores
    .replace(/-+/g, '_')           // Hyphens to underscores
    .replace(/_+/g, '_')           // Collapse multiple underscores
    .substring(0, 50)              // Limit length
    .replace(/_$/, '');            // Remove trailing underscore
};

// Icons
const PlusIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ChatbotAdminPage = () => {
  const { currentUser } = useAuth();
  const { reloadKnowledgeBase } = useChatbot();
  const [feedback, setFeedback] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ai-feedback');
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState(''); // Search for AI Feedback tab
  const [clearingHistory, setClearingHistory] = useState(false);
  const [lifetimeStats, setLifetimeStats] = useState({ lifetimeSessions: 0, lifetimeFeedback: 0 });
  const [kbSourceFilter, setKbSourceFilter] = useState('all'); // all, faq, manual
  const [kbCategoryFilter, setKbCategoryFilter] = useState('all'); // all, or specific category
  const [kbStatusFilter, setKbStatusFilter] = useState('active'); // all, active, disabled
  const [kbSortBy, setKbSortBy] = useState('priority'); // priority, title, createdAt, updatedAt
  
  // AI Feedback state
  const [chatFeedbackFilter, setChatFeedbackFilter] = useState('helpful');
  const [aiFeedbackCategoryFilter, setAiFeedbackCategoryFilter] = useState('all');
  const [promotingFeedback, setPromotingFeedback] = useState(null);
  
  // Inline note editing state for AI feedback
  const [notingAiFeedbackId, setNotingAiFeedbackId] = useState(null);
  const [aiFeedbackNoteText, setAiFeedbackNoteText] = useState('');
  const [savingAiFeedbackNote, setSavingAiFeedbackNote] = useState(false);
  
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    category: 'general',
    keywords: '',
    steps: '',
    priority: 10
  });

  const canManage = hasPermission(currentUser, FEATURE_PERMISSIONS.CHATBOT_MANAGE) || hasPermission(currentUser, FEATURE_PERMISSIONS.CHATBOT_VIEW) || canViewAllData(currentUser);
  const isReadOnly = !hasPermission(currentUser, FEATURE_PERMISSIONS.CHATBOT_MANAGE);

  // Real-time listener for feedback + fetch other data
  useEffect(() => {
    if (!canManage) return;
    
    setLoading(true);
    
    // Real-time listener for feedback
    const feedbackQuery = query(
      collection(db, collectionPath('chatFeedback')),
      orderBy('timestamp', 'desc'),
      limit(200)
    );
    
    const unsubscribeFeedback = onSnapshot(feedbackQuery, (snapshot) => {
      const feedbackData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));
      setFeedback(feedbackData);
      setLoading(false);
    }, (err) => {
      console.error('[ChatbotAdmin] Error listening to feedback:', err);
      setToast({ message: `Error loading feedback: ${err.message}`, type: 'error' });
      setLoading(false);
    });
    
    // Fetch other data (one-time)
    const fetchOtherData = async () => {
      try {
        // Fetch chat sessions for response rate calculation
        const sessionsQuery = query(
          collection(db, collectionPath('chatSessions')),
          orderBy('timestamp', 'desc'),
          limit(500)
        );
        const sessionsSnap = await getDocs(sessionsQuery);
        setChatSessions(sessionsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));

        // Load lifetime stats
        try {
          const statsDoc = await getDoc(doc(db, collectionPath('config'), 'chatbotStats'));
          if (statsDoc.exists()) {
            setLifetimeStats(statsDoc.data());
          } else {
            // Seed from current counts if stats doc doesn't exist yet
            setLifetimeStats({
              lifetimeSessions: sessionsSnap.size,
              lifetimeFeedback: 0 // Will be set from feedback listener
            });
          }
        } catch (err) {
          console.warn('[ChatbotAdmin] Could not load lifetime stats:', err);
        }

        // Fetch knowledge base
        const knowledgeQuery = query(
          collection(db, collectionPath('chatbotKnowledge')),
          orderBy('priority', 'desc'),
          limit(200)
        );
        const knowledgeSnap = await getDocs(knowledgeQuery);
        const knowledgeData = knowledgeSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setKnowledge(knowledgeData);

      } catch (err) {
        console.error('[ChatbotAdmin] Error fetching data:', err);
        setToast({ message: `Error loading data: ${err.message}`, type: 'error' });
      }
    };
    
    fetchOtherData();
    
    // Cleanup listener on unmount
    return () => unsubscribeFeedback();
  }, [canManage]);

  // AI-only feedback subset
  const aiFeedback = useMemo(() => feedback.filter(f => f.aiPowered), [feedback]);

  // Unique page categories from AI feedback for filter
  const aiFeedbackCategories = useMemo(() => {
    const pages = new Set(aiFeedback.map(f => f.page).filter(Boolean));
    return Array.from(pages).sort();
  }, [aiFeedback]);

  // Filter AI feedback for the Feedback tab
  const filteredAiFeedback = useMemo(() => {
    let filtered = aiFeedback;
    // Category (page) filter
    if (aiFeedbackCategoryFilter !== 'all') {
      filtered = filtered.filter(f => f.page === aiFeedbackCategoryFilter);
    }
    // Status filter
    switch (chatFeedbackFilter) {
      case 'helpful':
        return filtered.filter(f => f.isHelpful);
      case 'not-helpful':
        return filtered.filter(f => !f.isHelpful);
      case 'promoted':
        return filtered.filter(f => f.promotedToKB);
      case 'unreviewed':
        return filtered.filter(f => !f.isHelpful && !f.reviewed);
      default: // 'all'
        return filtered;
    }
  }, [aiFeedback, chatFeedbackFilter, aiFeedbackCategoryFilter]);

  // Stats - AI-specific
  const stats = useMemo(() => {
    const total = aiFeedback.length;
    const helpful = aiFeedback.filter(f => f.isHelpful).length;
    const notHelpful = aiFeedback.filter(f => !f.isHelpful).length;
    const promoted = aiFeedback.filter(f => f.promotedToKB).length;
    const unreviewed = aiFeedback.filter(f => !f.isHelpful && !f.reviewed).length;
    const satisfactionRate = total > 0 ? ((helpful / total) * 100).toFixed(1) : 0;
    
    // Response rate = lifetime chatbot feedback / lifetime chat sessions
    const totalResponses = lifetimeStats.lifetimeSessions || chatSessions.length;
    const totalFeedbackCount = lifetimeStats.lifetimeFeedback || feedback.length;
    const responseRate = totalResponses > 0 ? Math.min(((totalFeedbackCount / totalResponses) * 100), 100).toFixed(1) : 0;
    
    return { total, helpful, notHelpful, promoted, unreviewed, satisfactionRate, totalResponses, responseRate, totalFeedback: totalFeedbackCount };
  }, [aiFeedback, feedback, chatSessions, lifetimeStats]);

  // Calculate satisfaction rate per knowledge article for KB table
  // Only uses matchedArticleId from feedback - no keyword matching to avoid false associations
  const articleSatisfaction = useMemo(() => {
    const satisfaction = {};
    feedback.forEach(f => {
      // Only count feedback where a specific article was used
      if (f.matchedArticleId) {
        if (!satisfaction[f.matchedArticleId]) {
          satisfaction[f.matchedArticleId] = { helpful: 0, total: 0 };
        }
        satisfaction[f.matchedArticleId].total++;
        if (f.isHelpful) satisfaction[f.matchedArticleId].helpful++;
      }
    });
    return satisfaction;
  }, [feedback, knowledge]);

  // Filtered and sorted knowledge
  const filteredKnowledge = useMemo(() => {
    let filtered = knowledge;
    
    // Source filter
    if (kbSourceFilter !== 'all') {
      filtered = filtered.filter(k => k.source === kbSourceFilter);
    }
    
    // Category filter
    if (kbCategoryFilter !== 'all') {
      filtered = filtered.filter(k => k.category === kbCategoryFilter);
    }
    
    // Status filter
    if (kbStatusFilter === 'active') {
      filtered = filtered.filter(k => k.isActive !== false);
    } else if (kbStatusFilter === 'disabled') {
      filtered = filtered.filter(k => k.isActive === false);
    }
    
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.title?.toLowerCase().includes(search) ||
        entry.content?.toLowerCase().includes(search) ||
        entry.keywords?.some(k => k.toLowerCase().includes(search))
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      switch (kbSortBy) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'createdAt':
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        case 'updatedAt':
          return (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0);
        case 'priority':
        default:
          return (b.priority || 0) - (a.priority || 0);
      }
    });
    
    return filtered;
  }, [knowledge, kbSourceFilter, kbCategoryFilter, kbStatusFilter, searchQuery, kbSortBy]);

  // Get unique categories from knowledge base
  const knowledgeCategories = useMemo(() => {
    const cats = new Set(knowledge.map(k => k.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [knowledge]);

  // Promote AI response to Knowledge Base
  const promoteToKnowledgeBase = async (feedbackItem, title, category, keywords) => {
    try {
      // Generate document ID following naming conventions
      const timestamp = new Date().toISOString().replace(/[:.]/g, '').substring(0, 15);
      const baseTitle = title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 30);
      const docId = `kb_ai_${baseTitle}_${timestamp}`;
      
      // Create knowledge base entry
      await setDoc(doc(db, collectionPath('chatbotKnowledge'), docId), {
        title: sanitizeString(title, 200),
        content: feedbackItem.assistantResponse,
        category: category || 'general',
        keywords: keywords.split(',').map(k => sanitizeString(k.trim(), 50)).filter(Boolean),
        steps: [],
        source: 'ai-feedback',
        priority: 15,
        isActive: true,
        promotedFromFeedbackId: feedbackItem.id,
        originalQuestion: feedbackItem.userQuestion,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Mark feedback as promoted
      await updateDoc(doc(db, collectionPath('chatFeedback'), feedbackItem.id), {
        promotedToKB: true,
        promotedAt: serverTimestamp(),
        promotedBy: currentUser.uid,
        promotedKBId: docId
      });

      // Log promotion to Knowledge Base
      await logUserAction({
        action: 'Chatbot Knowledge Promoted',
        details: `Promoted AI response to KB: "${title}" (category: ${category || 'general'})`,
        entityType: 'ChatbotKnowledge',
        entityId: docId
      });

      // Update local state
      setFeedback(prev => prev.map(f => 
        f.id === feedbackItem.id ? { ...f, promotedToKB: true, promotedKBId: docId } : f
      ));
      
      // Add to local knowledge list
      setKnowledge(prev => [{
        id: docId,
        title: sanitizeString(title, 200),
        content: feedbackItem.assistantResponse,
        category: category || 'general',
        keywords: keywords.split(',').map(k => sanitizeString(k.trim(), 50)).filter(Boolean),
        source: 'ai-feedback',
        priority: 15,
        isActive: true,
        createdAt: new Date()
      }, ...prev]);
      
      setToast({ message: 'AI response promoted to Knowledge Base!', type: 'success' });
      setPromotingFeedback(null);
      reloadKnowledgeBase?.();
    } catch (err) {
      console.error('Error promoting to KB:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Dismiss feedback (mark as reviewed without promoting)
  const dismissAiFeedback = async (feedbackId) => {
    if (isReadOnly) return;
    try {
      await updateDoc(doc(db, collectionPath('chatFeedback'), feedbackId), {
        reviewed: true,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid
      });

      await logUserAction({
        action: 'Chatbot Feedback Reviewed',
        details: `Marked chat feedback as reviewed (dismissed without promoting)`,
        entityType: 'ChatFeedback',
        entityId: feedbackId
      });

      setFeedback(prev => prev.map(f => 
        f.id === feedbackId ? { ...f, reviewed: true } : f
      ));
      
      setToast({ message: 'Feedback marked as reviewed', type: 'success' });
    } catch (err) {
      console.error('Error dismissing feedback:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Add admin notes to AI feedback (marks as reviewed too)
  const addNoteAiFeedback = async (feedbackId, notes) => {
    if (isReadOnly) return;
    try {
      await updateDoc(doc(db, collectionPath('chatFeedback'), feedbackId), {
        adminNotes: notes,
        reviewed: true,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid
      });

      await logUserAction({
        action: 'Chatbot Feedback Note Added',
        details: `Added admin note to chat feedback and marked reviewed`,
        entityType: 'ChatFeedback',
        entityId: feedbackId
      });

      setFeedback(prev => prev.map(f =>
        f.id === feedbackId ? { ...f, adminNotes: notes, reviewed: true } : f
      ));
      setToast({ message: 'Notes saved and feedback marked as reviewed', type: 'success' });
    } catch (err) {
      console.error('Error adding note to AI feedback:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Save inline admin note on AI feedback
  const handleSaveAiFeedbackNote = async (fbId) => {
    if (!aiFeedbackNoteText.trim()) return;
    setSavingAiFeedbackNote(true);
    try {
      await addNoteAiFeedback(fbId, aiFeedbackNoteText.trim());
      setNotingAiFeedbackId(null);
      setAiFeedbackNoteText('');
    } catch (err) { /* handled by parent */ }
    finally { setSavingAiFeedbackNote(false); }
  };

  // Clear old chat sessions (older than 30 days)
  const handleClearOldSessions = async () => {
    if (isReadOnly) return;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const oldSessions = chatSessions.filter(s => {
      const date = s.timestamp?.toDate?.() || new Date(s.timestamp);
      return date < thirtyDaysAgo;
    });
    
    if (oldSessions.length === 0) {
      setToast({ message: 'No sessions older than 30 days to delete', type: 'success' });
      return;
    }
    
    if (!window.confirm(`Delete ${oldSessions.length} chat sessions older than 30 days? This cannot be undone.`)) {
      return;
    }
    
    setClearingHistory(true);
    try {
      const batch = writeBatch(db);
      let batchCount = 0;
      
      for (const session of oldSessions) {
        batch.delete(doc(db, collectionPath('chatSessions'), session.id));
        batchCount++;
        
        // Firestore batches limited to 500
        if (batchCount >= 450) {
          await batch.commit();
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }

      await logUserAction({
        action: 'Chatbot Sessions Purged',
        details: `Deleted ${oldSessions.length} chat sessions older than 30 days`,
        entityType: 'ChatSession'
      });

      // Update local state
      setChatSessions(prev => prev.filter(s => {
        const date = s.timestamp?.toDate?.() || new Date(s.timestamp);
        return date >= thirtyDaysAgo;
      }));
      
      setToast({ message: `Deleted ${oldSessions.length} old chat sessions`, type: 'success' });
    } catch (err) {
      console.error('[ChatbotAdmin] Error clearing sessions:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    } finally {
      setClearingHistory(false);
    }
  };

  // Add knowledge entry
  const handleAddKnowledge = async () => {
    if (!newEntry.title || !newEntry.content) {
      setToast({ message: 'Title and content are required', type: 'error' });
      return;
    }

    try {
      const keywords = newEntry.keywords
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 0);

      // Generate descriptive document ID from title
      const slug = generateSlug(newEntry.title);
      const timestamp = Date.now().toString(36); // Short timestamp for uniqueness
      const docId = `kb_${slug}_${timestamp}`;

      const newEntryPayload = {
        title: sanitizeString(newEntry.title, 200),
        content: sanitizeString(newEntry.content, 2000),
        category: newEntry.category,
        keywords: keywords,
        source: 'manual',
        priority: parseInt(newEntry.priority) || 10,
        isActive: true,
        createdAt: serverTimestamp(),
        createdBy: currentUser.displayName || currentUser.email
      };
      await setDoc(doc(db, collectionPath('chatbotKnowledge'), docId), newEntryPayload);

      await logEntityChanges({
        oldObj: null,
        newObj: newEntryPayload,
        fields: ['title', 'category', 'priority', 'isActive', 'keywords'],
        entityType: 'ChatbotKnowledge',
        entityId: docId,
        user: currentUser,
        action: 'Created'
      });

      setShowAddModal(false);
      setNewEntry({ title: '', content: '', category: 'general', keywords: '', steps: '', priority: 10 });
      setToast({ message: 'Knowledge entry added!', type: 'success' });

      // Refresh knowledge list
      const knowledgeQuery = query(
        collection(db, collectionPath('chatbotKnowledge')),
        orderBy('priority', 'desc'),
        limit(200)
      );
      const knowledgeSnap = await getDocs(knowledgeQuery);
      setKnowledge(knowledgeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Generate a slug from text for document IDs
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '_')          // Spaces to underscores
      .replace(/-+/g, '_')           // Hyphens to underscores
      .replace(/_+/g, '_')           // Collapse multiple underscores
      .substring(0, 50)              // Limit length
      .replace(/_$/, '');            // Remove trailing underscore
  };

  // Toggle knowledge active status
  const toggleKnowledgeActive = async (entryId, currentStatus) => {
    try {
      await updateDoc(doc(db, collectionPath('chatbotKnowledge'), entryId), {
        isActive: !currentStatus,
        updatedAt: serverTimestamp()
      });

      await logUserAction({
        action: currentStatus ? 'Chatbot Knowledge Disabled' : 'Chatbot Knowledge Enabled',
        details: `Knowledge entry ${currentStatus ? 'disabled' : 'enabled'}`,
        entityType: 'ChatbotKnowledge',
        entityId: entryId
      });

      setKnowledge(prev => prev.map(k => 
        k.id === entryId ? { ...k, isActive: !currentStatus } : k
      ));
      
      // Immediately update chatbot's knowledge base
      if (reloadKnowledgeBase) {
        await reloadKnowledgeBase();
      }
      
      setToast({ message: currentStatus ? 'Entry disabled - removed from chatbot' : 'Entry enabled - added to chatbot', type: 'success' });
    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Delete knowledge entry
  const handleDeleteKnowledge = async (entryId) => {
    try {
      const existing = knowledge.find(k => k.id === entryId);
      await deleteDoc(doc(db, collectionPath('chatbotKnowledge'), entryId));

      await logEntityDeletion({
        user: currentUser,
        entityType: 'ChatbotKnowledge',
        entityId: entryId,
        entityName: existing?.title || entryId
      });

      setKnowledge(prev => prev.filter(k => k.id !== entryId));
      setConfirmDelete(null);
      setSelectedEntry(null);
      
      // Immediately update chatbot's knowledge base
      if (reloadKnowledgeBase) {
        await reloadKnowledgeBase();
      }
      
      setToast({ message: 'Entry deleted', type: 'success' });
    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Open edit modal
  const openEditModal = (entry) => {
    setEditingEntry({
      ...entry,
      keywords: entry.keywords?.join(', ') || '',
      steps: entry.steps?.join('\n') || ''
    });
    setShowEditModal(true);
  };

  // Save edited entry
  const handleEditKnowledge = async () => {
    if (!editingEntry.title || !editingEntry.content) {
      setToast({ message: 'Title and content are required', type: 'error' });
      return;
    }

    try {
      const keywords = editingEntry.keywords
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 0);

      const steps = editingEntry.steps
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const updatedPayload = {
        title: sanitizeString(editingEntry.title, 200),
        content: sanitizeString(editingEntry.content, 2000),
        category: editingEntry.category,
        keywords: keywords,
        steps: steps.length > 0 ? steps : null,
        priority: parseInt(editingEntry.priority) || 10,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.displayName || currentUser.email
      };
      const previousEntry = knowledge.find(k => k.id === editingEntry.id) || {};
      await updateDoc(doc(db, collectionPath('chatbotKnowledge'), editingEntry.id), updatedPayload);

      await logEntityChanges({
        oldObj: previousEntry,
        newObj: { ...previousEntry, ...updatedPayload },
        fields: ['title', 'category', 'priority', 'keywords', 'content'],
        entityType: 'ChatbotKnowledge',
        entityId: editingEntry.id,
        user: currentUser,
        action: 'Updated'
      });

      setKnowledge(prev => prev.map(k => 
        k.id === editingEntry.id 
          ? { ...k, ...editingEntry, keywords, steps: steps.length > 0 ? steps : null }
          : k
      ));

      setShowEditModal(false);
      setEditingEntry(null);
      setSelectedEntry(null);
      
      // Immediately update chatbot's knowledge base
      if (reloadKnowledgeBase) {
        await reloadKnowledgeBase();
      }
      
      setToast({ message: 'Entry updated!', type: 'success' });
    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <strong>Permission Denied:</strong> You do not have access to Chatbot Administration.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chatbot Administration</h1>
          <p className="text-gray-600 mt-1">Review feedback, manage knowledge base, and configure settings</p>
        </div>
        <div className="flex gap-2">
          {!isReadOnly && (
          <Tooltip text="Add custom knowledge entry">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <PlusIcon className="w-5 h-5" />
            Add Entry
          </button>
          </Tooltip>
          )}
        </div>
      </div>

      {/* Stats Cards - AI Feedback */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div 
          onClick={() => { setActiveTab('ai-feedback'); setChatFeedbackFilter('all'); }}
          className="bg-white p-4 rounded-lg shadow border-l-4 border-emerald-500 cursor-pointer hover:bg-emerald-50 transition-colors"
        >
          <div className="text-sm text-gray-600">AI Feedback</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>
        <div 
          onClick={() => { setActiveTab('ai-feedback'); setChatFeedbackFilter('helpful'); }}
          className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500 cursor-pointer hover:bg-green-50 transition-colors"
        >
          <div className="text-sm text-gray-600">AI Helpful</div>
          <div className="text-2xl font-bold text-green-600">{stats.helpful}</div>
        </div>
        <div 
          onClick={() => { setActiveTab('ai-feedback'); setChatFeedbackFilter('not-helpful'); }}
          className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500 cursor-pointer hover:bg-red-50 transition-colors"
        >
          <div className="text-sm text-gray-600">AI Not Helpful</div>
          <div className="text-2xl font-bold text-red-600">{stats.notHelpful}</div>
        </div>
        <div 
          onClick={() => { setActiveTab('ai-feedback'); setChatFeedbackFilter('promoted'); }}
          className="bg-white p-4 rounded-lg shadow border-l-4 border-indigo-500 cursor-pointer hover:bg-indigo-50 transition-colors"
        >
          <div className="text-sm text-gray-600">Promoted to KB</div>
          <div className="text-2xl font-bold text-indigo-600">{stats.promoted}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="text-sm text-gray-600">AI Satisfaction</div>
          <div className="text-2xl font-bold text-purple-600">{stats.satisfactionRate}%</div>
          <div className="text-xs text-gray-400">{stats.helpful} of {stats.total} helpful</div>
        </div>
        <Tooltip text="Percentage of chatbot sessions where a user rated the response. Based on lifetime totals.">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-cyan-500 h-full">
            <div className="text-sm text-gray-600">Response Rate</div>
            <div className="text-2xl font-bold text-cyan-600">{stats.responseRate}%</div>
            <div className="text-xs text-gray-400">{stats.totalFeedback} of {stats.totalResponses} rated</div>
          </div>
        </Tooltip>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('ai-feedback')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ai-feedback'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🤖 AI Feedback ({aiFeedback.length})
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'knowledge'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📚 AI Knowledge Base ({knowledge.length})
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📈 Satisfaction Reports
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sessions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            💬 Chat Sessions ({chatSessions.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            ⚙️ Scoring & Settings
          </button>
          
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {/* Knowledge Tab */}
          {activeTab === 'knowledge' && (
            <KnowledgeBaseList
              knowledge={knowledge}
              filteredKnowledge={filteredKnowledge}
              categories={knowledgeCategories}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sourceFilter={kbSourceFilter}
              setSourceFilter={setKbSourceFilter}
              categoryFilter={kbCategoryFilter}
              setCategoryFilter={setKbCategoryFilter}
              statusFilter={kbStatusFilter}
              setStatusFilter={setKbStatusFilter}
              sortBy={kbSortBy}
              setSortBy={setKbSortBy}
              articleSatisfaction={articleSatisfaction}
              onEdit={isReadOnly ? null : openEditModal}
              onDelete={isReadOnly ? null : (entry) => setConfirmDelete(entry)}
              onToggleActive={isReadOnly ? null : toggleKnowledgeActive}
            />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              {/* Category Satisfaction */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Satisfaction by Category</h3>
                <p className="text-sm text-gray-600 mb-4">
                  How satisfied users are with responses in each knowledge category
                </p>
                <div className="space-y-3">
                  {(() => {
                    const categoryStats = {};
                    feedback.forEach(f => {
                      // Get category from the matched knowledge entry
                      const matchedKB = knowledge.find(k => 
                        f.assistantResponse?.includes(k.title) || 
                        k.keywords?.some(kw => f.userQuestion?.toLowerCase().includes(kw))
                      );
                      const category = matchedKB?.category || 'general';
                      if (!categoryStats[category]) {
                        categoryStats[category] = { helpful: 0, notHelpful: 0 };
                      }
                      if (f.isHelpful) categoryStats[category].helpful++;
                      else categoryStats[category].notHelpful++;
                    });
                    
                    return Object.entries(categoryStats)
                      .sort((a, b) => (b[1].helpful + b[1].notHelpful) - (a[1].helpful + a[1].notHelpful))
                      .map(([category, data]) => {
                        const total = data.helpful + data.notHelpful;
                        const rate = total > 0 ? ((data.helpful / total) * 100).toFixed(0) : 0;
                        return (
                          <div key={category} className="flex items-center gap-4">
                            <div className="w-32 font-medium capitalize">{category.replace('_', ' ')}</div>
                            <div className="flex-1">
                              <div className="h-6 bg-gray-200 rounded-full overflow-hidden flex">
                                <div 
                                  className="h-full bg-green-500 transition-all"
                                  style={{ width: `${rate}%` }}
                                />
                                <div 
                                  className="h-full bg-red-500 transition-all"
                                  style={{ width: `${100 - rate}%` }}
                                />
                              </div>
                            </div>
                            <div className="w-20 text-sm text-right">
                              <span className="text-green-600">{data.helpful}</span>
                              {' / '}
                              <span className="text-red-600">{data.notHelpful}</span>
                            </div>
                            <div className="w-16 text-sm font-medium text-right">{rate}%</div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>

              {/* User Feedback Summary */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Feedback by User</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Individual user satisfaction ratings
                </p>
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-[40%] px-3 py-2 text-left font-medium text-gray-500">User</th>
                        <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">👍</th>
                        <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">👎</th>
                        <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">Total</th>
                        <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        const userStats = {};
                        feedback.forEach(f => {
                          const user = f.userName || 'Unknown';
                          if (!userStats[user]) userStats[user] = { helpful: 0, notHelpful: 0 };
                          if (f.isHelpful) userStats[user].helpful++;
                          else userStats[user].notHelpful++;
                        });
                        
                        return Object.entries(userStats)
                          .sort((a, b) => (b[1].helpful + b[1].notHelpful) - (a[1].helpful + a[1].notHelpful))
                          .map(([user, data]) => {
                            const total = data.helpful + data.notHelpful;
                            const rate = total > 0 ? ((data.helpful / total) * 100).toFixed(0) : 0;
                            return (
                              <tr key={user}>
                                <td className="px-3 py-2 font-medium">{user}</td>
                                <td className="px-3 py-2 text-green-600">{data.helpful}</td>
                                <td className="px-3 py-2 text-red-600">{data.notHelpful}</td>
                                <td className="px-3 py-2">{total}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    rate >= 70 ? 'bg-green-100 text-green-700' :
                                    rate >= 50 ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {rate}%
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Word Cloud */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Frequently Asked Keywords</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Most common words in user questions (larger = more frequent)
                </p>
                <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg min-h-[200px] items-center justify-center">
                  {(() => {
                    const wordCounts = {};
                    const stopWords = new Set(['how', 'what', 'where', 'when', 'why', 'who', 'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'has', 'have', 'been', 'from', 'this', 'that', 'with', 'they', 'will', 'would', 'there', 'their', 'which', 'about', 'into', 'them', 'these', 'some']);
                    
                    feedback.forEach(f => {
                      const words = (f.userQuestion || '')
                        .toLowerCase()
                        .replace(/[^a-z\s]/g, '')
                        .split(/\s+/)
                        .filter(w => w.length > 2 && !stopWords.has(w));
                      
                      words.forEach(w => {
                        wordCounts[w] = (wordCounts[w] || 0) + 1;
                      });
                    });
                    
                    const sorted = Object.entries(wordCounts)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 50);
                    
                    if (sorted.length === 0) {
                      return <p className="text-gray-400">No feedback data yet</p>;
                    }
                    
                    const maxCount = sorted[0][1];
                    const minCount = sorted[sorted.length - 1]?.[1] || 1;
                    
                    return sorted.map(([word, count]) => {
                      // Scale font size from 0.75rem to 2rem based on frequency
                      const scale = minCount === maxCount ? 1 : (count - minCount) / (maxCount - minCount);
                      const fontSize = 0.75 + scale * 1.25;
                      const opacity = 0.5 + scale * 0.5;
                      
                      // Color based on associated feedback
                      const relatedFeedback = feedback.filter(f => 
                        f.userQuestion?.toLowerCase().includes(word)
                      );
                      const helpfulCount = relatedFeedback.filter(f => f.isHelpful).length;
                      const unhelpfulCount = relatedFeedback.length - helpfulCount;
                      const isPositive = helpfulCount >= unhelpfulCount;
                      
                      return (
                        <Tooltip key={word} text={`${count} questions, ${helpfulCount}👍 ${unhelpfulCount}👎`}>
                          <span
                            className={`px-2 py-1 rounded cursor-default transition-transform hover:scale-110 ${
                              isPositive ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                            }`}
                            style={{ fontSize: `${fontSize}rem`, opacity }}
                          >
                            {word}
                          </span>
                        </Tooltip>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Helpful vs Not Helpful Over Time */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Feedback Trend (Last 30 Days)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Daily helpful vs not helpful feedback
                </p>
                <div className="h-48 flex items-end gap-1">
                  {(() => {
                    // Group by date
                    const dailyStats = {};
                    const today = new Date();
                    for (let i = 29; i >= 0; i--) {
                      const date = new Date(today);
                      date.setDate(date.getDate() - i);
                      const key = date.toISOString().split('T')[0];
                      dailyStats[key] = { helpful: 0, notHelpful: 0 };
                    }
                    
                    feedback.forEach(f => {
                      const date = f.timestamp?.toISOString?.()?.split('T')[0] || 
                                   f.timestamp?.toDate?.()?.toISOString?.()?.split('T')[0];
                      if (date && dailyStats[date]) {
                        if (f.isHelpful) dailyStats[date].helpful++;
                        else dailyStats[date].notHelpful++;
                      }
                    });
                    
                    const maxVal = Math.max(
                      ...Object.values(dailyStats).map(d => d.helpful + d.notHelpful),
                      1
                    );
                    
                    return Object.entries(dailyStats).map(([date, data], idx) => {
                      const total = data.helpful + data.notHelpful;
                      const helpfulHeight = maxVal > 0 ? (data.helpful / maxVal) * 100 : 0;
                      const notHelpfulHeight = maxVal > 0 ? (data.notHelpful / maxVal) * 100 : 0;
                      
                      return (
                        <Tooltip key={date} text={`${date}: ${data.helpful}👍 ${data.notHelpful}👎`}>
                          <div className="flex-1 flex flex-col items-center gap-0.5">
                            <div 
                              className="w-full bg-green-500 rounded-t"
                              style={{ height: `${helpfulHeight}%`, minHeight: data.helpful > 0 ? '4px' : '0' }}
                            />
                            <div 
                              className="w-full bg-red-500 rounded-b"
                              style={{ height: `${notHelpfulHeight}%`, minHeight: data.notHelpful > 0 ? '4px' : '0' }}
                            />
                          </div>
                        </Tooltip>
                      );
                    });
                  })()}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>30 days ago</span>
                  <span>Today</span>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Scoring Formula */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Scoring Formula</h3>
                <p className="text-sm text-gray-600 mb-4">
                  How the chatbot ranks search results when answering questions:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Word Match Score</span>
                    <span className="font-mono text-gray-600">+1 per matching word</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>FAQ Source Boost</span>
                    <span className="font-mono text-gray-600">+2</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Page Category Boost</span>
                    <span className="font-mono text-gray-600">+2 (when page matches category)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Feedback Score</span>
                    <span className="font-mono text-gray-600">+0.5 × (helpful - unhelpful)</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>Priority Bonus</span>
                    <span className="font-mono text-gray-600">priority / 10</span>
                  </div>
                </div>
              </div>

              {/* Keyword Feedback Scores */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-2">Keyword Feedback Scores</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Keywords from user questions with their feedback-based scores. Positive scores boost results, negative scores penalize.
                </p>
                {feedback.length > 0 ? (
                  <div className="max-h-[600px] overflow-y-auto">
                    <table className="w-full table-fixed divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="w-[55%] px-3 py-2 text-left font-medium text-gray-500">Keyword</th>
                          <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">👍</th>
                          <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">👎</th>
                          <th className="w-[15%] px-3 py-2 text-left font-medium text-gray-500">Net</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(() => {
                          const scores = {};
                          feedback.forEach(f => {
                            const words = (f.userQuestion || '').toLowerCase().split(/\s+/).filter(w => w.length > 3);
                            words.forEach(w => {
                              if (!scores[w]) scores[w] = { helpful: 0, unhelpful: 0 };
                              if (f.isHelpful) scores[w].helpful++;
                              else scores[w].unhelpful++;
                            });
                          });
                          return Object.entries(scores)
                            .map(([kw, s]) => ({ kw, ...s, net: s.helpful - s.unhelpful }))
                            .sort((a, b) => (b.helpful + b.unhelpful) - (a.helpful + a.unhelpful))
                            .map(({ kw, helpful, unhelpful, net }) => (
                              <tr key={kw}>
                                <td className="px-3 py-1.5 font-medium">{kw}</td>
                                <td className="px-3 py-1.5 text-green-600">{helpful}</td>
                                <td className="px-3 py-1.5 text-red-600">{unhelpful}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                                    net > 0 ? 'bg-green-100 text-green-700' :
                                    net < 0 ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {net > 0 ? '+' : ''}{net}
                                  </span>
                                </td>
                              </tr>
                            ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No feedback data yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Chat Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="space-y-6">
              {/* Session Management Header */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">Chat Session History</h3>
                    <p className="text-sm text-gray-600">
                      Total recent sessions: {chatSessions.length} | Manage chat history stored in Firestore. Retains lifetime stats but removes session details for older chats.
                    </p>
                  </div>
                  <Tooltip text="Delete chat sessions older than 30 days">
                    <button
                      onClick={handleClearOldSessions}
                      disabled={clearingHistory || isReadOnly}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {clearingHistory ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      )}
                      Clear Old Sessions (30+ days)
                    </button>
                  </Tooltip>
                </div>

                {/* Stats summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="text-xs text-blue-600">Total Sessions</div>
                    <div className="text-xl font-bold text-blue-700">{chatSessions.length}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs text-green-600">Last 7 Days</div>
                    <div className="text-xl font-bold text-green-700">
                      {chatSessions.filter(s => {
                        const date = s.timestamp?.toDate?.() || new Date(s.timestamp);
                        return (Date.now() - date.getTime()) < 7 * 24 * 60 * 60 * 1000;
                      }).length}
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <div className="text-xs text-amber-600">Last 30 Days</div>
                    <div className="text-xl font-bold text-amber-700">
                      {chatSessions.filter(s => {
                        const date = s.timestamp?.toDate?.() || new Date(s.timestamp);
                        return (Date.now() - date.getTime()) < 30 * 24 * 60 * 60 * 1000;
                      }).length}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="text-xs text-red-600">Older than 30 Days</div>
                    <div className="text-xl font-bold text-red-700">
                      {chatSessions.filter(s => {
                        const date = s.timestamp?.toDate?.() || new Date(s.timestamp);
                        return (Date.now() - date.getTime()) >= 30 * 24 * 60 * 60 * 1000;
                      }).length}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Sessions List */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">10 Most Recent Chat Sessions</h3>
                {chatSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-3">💬</div>
                    <div>No chat sessions recorded yet</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatSessions.slice(0, 10).map((session, idx) => {
                      const timestamp = session.timestamp?.toDate?.() || new Date(session.timestamp);
                      return (
                        <div key={session.id || idx} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{session.userName || 'Unknown User'}</span>
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{session.page || 'N/A'}</span>
                              {session.category && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{session.category}</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {timestamp.toLocaleString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-blue-50 rounded p-2">
                              <div className="text-xs text-blue-600 font-medium mb-1">User Question:</div>
                              <div className="text-sm text-gray-700 line-clamp-2">{session.userMessage}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-xs text-gray-600 font-medium mb-1">Assistant Response:</div>
                              <div className="text-sm text-gray-700 line-clamp-2">{session.assistantMessage}</div>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-400 font-mono">
                            ID: {session.id}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Feedback Tab — Inline UI matching FAQ Feedback style */}
          {activeTab === 'ai-feedback' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">AI Chatbot Feedback</h3>
                <p className="text-sm text-gray-600">Review AI-generated responses — promote helpful answers to Knowledge Base</p>
              </div>

              {/* Summary Cards — clickable */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div
                  onClick={() => setChatFeedbackFilter('all')}
                  className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    chatFeedbackFilter === 'all' ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-200'
                  }`}
                >
                  <div className="text-sm text-gray-600 mb-1">Total AI Feedback</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                </div>
                <div
                  onClick={() => setChatFeedbackFilter('helpful')}
                  className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    chatFeedbackFilter === 'helpful' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200'
                  }`}
                >
                  <div className="text-sm text-gray-600 mb-1">Helpful</div>
                  <div className="text-2xl font-bold text-green-600">{stats.helpful}</div>
                </div>
                <div
                  onClick={() => setChatFeedbackFilter('not-helpful')}
                  className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    chatFeedbackFilter === 'not-helpful' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'
                  }`}
                >
                  <div className="text-sm text-gray-600 mb-1">Not Helpful</div>
                  <div className="text-2xl font-bold text-red-600">{stats.notHelpful}</div>
                </div>
                <div
                  onClick={() => setChatFeedbackFilter('promoted')}
                  className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    chatFeedbackFilter === 'promoted' ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-100' : 'border-indigo-200 bg-indigo-50'
                  }`}
                >
                  <div className="text-sm text-indigo-700 mb-1">Promoted to KB</div>
                  <div className="text-2xl font-bold text-indigo-600">{stats.promoted}</div>
                </div>
                <div
                  onClick={() => setChatFeedbackFilter('unreviewed')}
                  className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    chatFeedbackFilter === 'unreviewed' ? 'border-amber-500 ring-2 ring-amber-200 bg-amber-100' : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <div className="text-sm text-amber-700 mb-1">Unreviewed</div>
                  <div className="text-2xl font-bold text-amber-600">{stats.unreviewed}</div>
                </div>
              </div>

              {/* Filter + Category + Search */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <select
                  value={chatFeedbackFilter}
                  onChange={(e) => setChatFeedbackFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All AI Feedback</option>
                  <option value="helpful">Helpful</option>
                  <option value="not-helpful">Not Helpful</option>
                  <option value="promoted">Promoted to KB</option>
                  <option value="unreviewed">Unreviewed</option>
                </select>
                <select
                  value={aiFeedbackCategoryFilter}
                  onChange={(e) => setAiFeedbackCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">All Pages</option>
                  {aiFeedbackCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Search by question, response, user, or comment..."
                  value={feedbackSearch}
                  onChange={(e) => setFeedbackSearch(e.target.value)}
                  className={`flex-1 w-full sm:w-auto px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    feedbackSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {(() => {
                    let items = filteredAiFeedback;
                    if (feedbackSearch) {
                      const q = feedbackSearch.toLowerCase();
                      items = items.filter(fb =>
                        (fb.userName || '').toLowerCase().includes(q) ||
                        (fb.userQuestion || '').toLowerCase().includes(q) ||
                        (fb.assistantResponse || '').toLowerCase().includes(q) ||
                        (fb.comment || '').toLowerCase().includes(q) ||
                        (fb.page || '').toLowerCase().includes(q)
                      );
                    }
                    return items.length;
                  })()} items
                </span>
              </div>

              {/* Feedback cards */}
              <div className="space-y-3">
                {(() => {
                  let displayedFeedback = filteredAiFeedback;
                  if (feedbackSearch) {
                    const q = feedbackSearch.toLowerCase();
                    displayedFeedback = displayedFeedback.filter(fb =>
                      (fb.userName || '').toLowerCase().includes(q) ||
                      (fb.userQuestion || '').toLowerCase().includes(q) ||
                      (fb.assistantResponse || '').toLowerCase().includes(q) ||
                      (fb.comment || '').toLowerCase().includes(q) ||
                      (fb.page || '').toLowerCase().includes(q)
                    );
                  }
                  return displayedFeedback.map(fb => (
                    <div
                      key={fb.id}
                      className={`border rounded-lg p-4 bg-white ${
                        fb.promotedToKB ? 'border-indigo-300 bg-indigo-50' :
                        fb.isHelpful ? 'border-emerald-200' :
                        'border-red-200'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Badges */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                              🤖 AI Response
                            </span>
                            {fb.isHelpful ? (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                                👍 Helpful
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                                👎 Not Helpful
                              </span>
                            )}
                            {fb.promotedToKB && (
                              <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                                ✅ Promoted to KB
                              </span>
                            )}
                            {!fb.isHelpful && !fb.reviewed && (
                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                                Unreviewed
                              </span>
                            )}
                            {fb.reviewed && !fb.promotedToKB && (
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                ✓ Reviewed
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {fb.page || 'General'}
                            </span>
                          </div>

                          {/* User question */}
                          <div className="mb-3">
                            <div className="text-xs font-medium text-gray-500 mb-1">User Question:</div>
                            <p className="text-sm font-medium text-gray-900 bg-gray-100 rounded p-2">
                              {fb.userQuestion || 'No question recorded'}
                            </p>
                          </div>

                          {/* AI response */}
                          <div className="mb-3">
                            <div className="text-xs font-medium text-gray-500 mb-1">AI Response:</div>
                            <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 max-h-40 overflow-y-auto">
                              {fb.assistantResponse?.substring(0, 500)}
                              {fb.assistantResponse?.length > 500 && '...'}
                            </div>
                          </div>

                          {/* User comment */}
                          {fb.comment && (
                            <div className="mb-2">
                              <div className="text-xs font-medium text-gray-500 mb-1">User Comment:</div>
                              <p className="text-sm text-red-700 italic bg-red-50 rounded p-2">
                                &ldquo;{fb.comment}&rdquo;
                              </p>
                            </div>
                          )}

                          {/* Admin notes */}
                          {fb.adminNotes && (
                            <div className="mb-2">
                              <div className="text-xs font-medium text-gray-500 mb-1">Admin Notes:</div>
                              <p className="text-sm text-indigo-700 bg-indigo-50 rounded p-2">
                                {fb.adminNotes}
                              </p>
                            </div>
                          )}

                          {/* Meta */}
                          <div className="text-xs text-gray-500 flex gap-4 flex-wrap">
                            <span>By: {fb.userName || 'Unknown'}</span>
                            <span>{fb.timestamp ? new Date(fb.timestamp).toLocaleString() : 'Unknown date'}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          {!isReadOnly && fb.isHelpful && !fb.promotedToKB && (
                            <Tooltip text="Promote this AI response to Knowledge Base">
                              <button
                                onClick={() => setPromotingFeedback(fb)}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                              >
                                Promote to KB
                              </button>
                            </Tooltip>
                          )}
                          {!isReadOnly && !fb.isHelpful && !fb.reviewed && !fb.promotedToKB && (
                            <Tooltip text="Mark as reviewed without promoting">
                              <button
                                onClick={() => dismissAiFeedback(fb.id)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                              >
                                Dismiss
                              </button>
                            </Tooltip>
                          )}
                          {!isReadOnly && !fb.isHelpful && (
                            notingAiFeedbackId === fb.id ? (
                              <div className="flex flex-col gap-2 w-56">
                                <textarea
                                  value={aiFeedbackNoteText}
                                  onChange={(e) => setAiFeedbackNoteText(e.target.value)}
                                  placeholder="What should be improved..."
                                  className="text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 resize-none"
                                  rows={3}
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveAiFeedbackNote(fb.id)}
                                    disabled={savingAiFeedbackNote || !aiFeedbackNoteText.trim()}
                                    className="flex-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50"
                                  >
                                    {savingAiFeedbackNote ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => { setNotingAiFeedbackId(null); setAiFeedbackNoteText(''); }}
                                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <Tooltip text={fb.adminNotes ? 'Edit admin notes' : 'Add notes about what should be improved'}>
                                <button
                                  onClick={() => { setNotingAiFeedbackId(fb.id); setAiFeedbackNoteText(fb.adminNotes || ''); }}
                                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm"
                                >
                                  {fb.adminNotes ? '✏️ Edit Notes' : '📝 Add Notes'}
                                </button>
                              </Tooltip>
                            )
                          )}
                          {fb.promotedToKB && fb.promotedKBId && (
                            <button
                              onClick={() => {
                                setActiveTab('knowledge');
                                const kbEntry = knowledge.find(k => k.id === fb.promotedKBId);
                                setSearchQuery(kbEntry?.title || fb.promotedKBId);
                              }}
                              className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm"
                            >
                              View in KB
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {filteredAiFeedback.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  {chatFeedbackFilter === 'helpful'
                    ? 'No AI responses with positive feedback awaiting promotion.'
                    : 'No feedback matching your filter.'
                  }
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add Knowledge Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Add Knowledge Entry</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newEntry.title}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., How to create a customer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newEntry.content}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={5}
                    placeholder="Detailed answer or explanation..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Supports markdown formatting</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newEntry.category}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="general">General</option>
                    <option value="navigation">Navigation</option>
                    <option value="customers">Customers</option>
                    <option value="opportunities">Opportunities</option>
                    <option value="orders">Orders</option>
                    <option value="tasks">Tasks</option>
                    <option value="reports">Reports</option>
                    <option value="email_marketing">Email Marketing</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                  <input
                    type="text"
                    value={newEntry.keywords}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, keywords: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="customer, create, new, add (comma-separated)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Score
                    <span className="text-xs text-gray-500 ml-1">(1-20, higher = more likely to show)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={newEntry.priority}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 5 (docs), 10 (FAQs), 15 (manual entries), 20 (critical)
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddKnowledge}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Knowledge Modal */}
      {showEditModal && editingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Edit Knowledge Entry</h2>
              
              {/* Info for FAQ entries */}
              {editingEntry.source === 'faq' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ FAQ Entry:</strong> Changes will update the chatbot's responses immediately. 
                    The Support Page will continue showing the original FAQ until the next app update.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingEntry.title}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., How to create a customer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editingEntry.content}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, content: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={5}
                    placeholder="Detailed answer or explanation..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Supports markdown formatting</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={editingEntry.category}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="general">General</option>
                    <option value="navigation">Navigation</option>
                    <option value="customers">Customers</option>
                    <option value="opportunities">Opportunities</option>
                    <option value="orders">Orders</option>
                    <option value="tasks">Tasks</option>
                    <option value="reports">Reports</option>
                    <option value="email_marketing">Email Marketing</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                  <input
                    type="text"
                    value={editingEntry.keywords}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, keywords: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="customer, create, new, add (comma-separated)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Steps (one per line)</label>
                  <textarea
                    value={editingEntry.steps}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, steps: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={4}
                    placeholder="Navigate to Customers page&#10;Click the + button&#10;Fill in the form"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Score
                    <span className="text-xs text-gray-500 ml-1">(1-20, higher = more likely to show)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={editingEntry.priority}
                    onChange={(e) => setEditingEntry(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => { setShowEditModal(false); setEditingEntry(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditKnowledge}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-2 text-red-600">Delete Knowledge Entry</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to permanently delete this entry?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="font-medium">{confirmDelete.title}</div>
                <div className="text-sm text-gray-500">
                  Source: {confirmDelete.source} • Category: {confirmDelete.category}
                </div>
              </div>
              <p className="text-sm text-red-600 mb-4">
                ⚠️ This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteKnowledge(confirmDelete.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promote to KB Modal */}
      {promotingFeedback && (
        <PromoteToKBModal
          feedback={promotingFeedback}
          categories={knowledgeCategories}
          onClose={() => setPromotingFeedback(null)}
          onPromote={isReadOnly ? null : promoteToKnowledgeBase}
        />
      )}
    </div>
  );
};

export default ChatbotAdminPage;
